import { Alert, Button, Col, Input, notification, Radio, Row, Spin, Tooltip } from "antd";
import React, { useEffect, useState } from "react";
import { httpService } from "../../base/services/httpService.service";
import { AiOutlineCheckCircle } from "react-icons/ai";
import { PlanDetail } from "../../pages/auth/register/components/planSection/PlanDetail";
import BasicModal from "../../ui/BasicModal/BasicModal";
import { ReactComponent as StarRewardRate } from "../../assets/icons/star-reward-rating-rate.svg";
import { ReactComponent as StarRewardBadge } from "../../assets/icons/star-award-reward-badge.svg";
import {
   OrganizationLevel,
   getCurrentOrganizationLevel
} from "../../base/services/accountLevel.service";
import AuthService from "../../base/services/authentication.service";
import {
   AccountLevel,
   getAccountLevel,
   getPlanAccountLevel,
   getPlanChangeOptions
} from "../../pages/auth/register/accountLevel";
import {
   ANNUAL_BILLING_NOTE,
   formatPrice,
   getPlanPrice
} from "../../pages/auth/register/planPricing";
import PromoCodePreview from "../Billing/PromoCodePreview";
import {
   NO_SUBSCRIPTION_LEFT_MESSAGE,
   describeCommitment,
   isCommittedPastThisPeriod,
   oldFlowCommitment,
   recoverFromNoSubscription,
   scheduledEndOf
} from "../Billing/billingActions";
import { PlanChangeTiming, billingApi } from "../Billing/billingApi";
import {
   BillingCode,
   SUPPORT_EMAIL,
   SubscriptionStatus,
   describeBillingError
} from "../Billing/billingCodes";
import { formatBillingDate } from "../Billing/billingDates";
import { rememberNewSubscription } from "../Billing/billingSignIn";
import {
   ENTERPRISE_ANNUAL_NOTE,
   ENTERPRISE_PACKET_ID,
   currentEnterpriseTerms,
   enterprisePlanOf,
   formatAmountBetween,
   formatEnterprisePrice,
   formatPlanPrice,
   hasEnterpriseTermsNow,
   isEnterpriseRequestOpen
} from "../Billing/enterprise";
import EnterpriseNeedsField from "../Billing/EnterpriseNeedsField";
import {
   ENTERPRISE_MESSAGE_NOT_SENT,
   EnterpriseRequestSource,
   notifyEnterpriseRequest,
   signedInRequester
} from "../Billing/enterpriseRequest";
import { useBillingStore } from "../Billing/billingStore";
import { settlePlanChangePayment } from "../Billing/paymentAction";
import {
   PlanChangeKind,
   UnavailableReason,
   describeOverLimits,
   estimateProration,
   formatAmount,
   changeAsAnswered,
   isPlanChangeOffered,
   planHeldIn,
   isSelfServicePacket,
   isYearlyPeriod,
   resolvePlanChange
} from "../Billing/planChanges";
import { isPlanDowngradesEnabled } from "../Billing/planDowngrades";
import { readPlanUsage } from "../Billing/planUsage";
import { useRecaptchaHeaders } from "../../hooks/useRecaptchaHeaders";

// `billingManaged` is whether the new billing flow is on for the organization
// (components/Billing/billingStore.js): a plan is then changed through POST
// /Payment/change-plan and a promo code can be checked first. With it off, the
// change goes through updateSubscriptionPlan, as it always did. Either way,
// every plan is offered.
//
// What choosing a plan does follows the rules in components/Billing/
// planChanges.js, on both flows, while they are switched on (planDowngrades.js):
// an upgrade changes now, with the difference charged today; a downgrade, or
// monthly → annual on the same packet, waits for the contract to end; a
// smaller plan the organization doesn't fit can't be chosen until it archives
// what it doesn't need. The server decides by the same rules, and the page
// reads back what it settled on. Switched off, every change is made now, as
// the backend does it today.
//
// An annual plan is billed monthly on a 12-month commitment. Before its last
// month it can't move to a monthly plan: change-plan and updateSubscriptionPlan
// both refuse that (COMMITMENT_ACTIVE), so the monthly plans are not offered
// while the subscription says so — its billing status on the new flow, its
// subscription read on the old one.
const TRIAL_CHANGE =
   "You're on a free trial, so the plan changes now. Your trial carries on, and the new plan's price applies when it ends.";

// A plan still on an old price billed once a year, changed now: Stripe bills
// the change at once, for a new monthly period.
const OLD_YEARLY_CHANGE =
   "The plan changes now. Your plan is still on a price billed once a year, so the change is billed today: the new plan's first month, less the unused part of your year. Anything left over stays as credit for your next invoices.";

// How long after a failed upgrade the status is read once more: when the
// payment went through but the switch didn't, the webhook completes it.
const UPGRADE_COMPLETION_READ_MS = 5000;

// While the subscription is paused, a trial that ended with no card.
const PAUSED_CHANGE =
   "Your subscription is paused until a card is added. The plan changes once you add one, and nothing is charged until then.";

// The cards call Standard "Starter".
const displayName = (name) => (name === "Standard" ? "Starter" : name);

// Why a plan can't be chosen when the page can't work out what choosing it does.
const UNAVAILABLE_REASONS = {
   [UnavailableReason.UNSUPPORTED]: "Your plan is managed by our team. Contact us to change it.",
   [UnavailableReason.MISSING_DATA]:
      "We couldn't load everything this change needs. Refresh the page and try again."
};

// The bank wants the payment confirmed on Stripe's invoice page. It is opened
// by the person's own click: a tab opened once the request has come back is a
// popup, which the browser may block.
const announceInvoiceToConfirm = (hostedInvoiceUrl) =>
   notification.warning({
      message: "Heads up!",
      description:
         "Your bank needs you to confirm the payment. Your plan changes once it has gone through.",
      btn: (
         <Button type="primary" href={hostedInvoiceUrl} target="_blank" rel="noopener noreferrer">
            Complete payment
         </Button>
      ),
      duration: 0
   });

// A plan cheaper than the one a cancelled subscription had (set to end, or
// ended): refused with DOWNGRADE_REQUIRES_SUPPORT, on either flow. Only support
// makes that change, so the way to them is offered, never a retry. The plans
// stay open: one at least the size of the current plan can still be picked.
const announceDowngradeNeedsSupport = () =>
   notification.warning({
      message: "Contact support to move to a smaller plan",
      description: (
         <>
            Your subscription has been cancelled, so it can't be moved to a smaller plan here.
            Please contact support at <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> to
            change to a smaller plan.
         </>
      ),
      duration: 0
   });

// A change for the end of the contract picked while the last payment is still
// owed: refused with PAYMENT_DUE, on either flow, and nothing is left pending.
// The way to settle it is on the same page: the payment notice at the top on
// the new flow (Update card, or Confirm payment), Edit payment method on the
// old one, which has no notice.
const announcePaymentDue = (billingManaged) => {
   const key = "plan-change-payment-due";
   const dueText = describeBillingError({ code: BillingCode.PAYMENT_DUE });

   // The page reads the status again after the refusal, so the notice is
   // looked for when the button is pressed, not when this opens.
   const showNotice = () => {
      notification.destroy(key);
      const notice = document.querySelector(".billing-notice--warning");
      if (notice) notice.scrollIntoView({ behavior: "smooth", block: "center" });
      else window.scrollTo({ top: 0, behavior: "smooth" });
   };

   notification.warning({
      key,
      message: "Heads up!",
      description: billingManaged
         ? dueText
         : `${dueText} Update your card with Edit payment method below.`,
      btn: billingManaged ? (
         <Button type="primary" onClick={showNotice}>
            Settle payment
         </Button>
      ) : undefined,
      duration: 0
   });
};

const SubscriptionPlanBoxes = ({
   planName,
   subscriptionStatus,
   subscriptionId,
   price,
   isMonthly,
   // From the subscription read: when the current period started and ends
   // (both flows), and — the old flow only — when an annual plan's commitment
   // ends and a change waiting for the end of the contract.
   subscriptionStartsAt,
   commitmentEnd,
   subscriptionEndsAt,
   cancelAt,
   pendingPlanChange: subscriptionPendingPlanChange,
   billingManaged,
   onPlanChanged
}) => {
   const recaptchaHeaders = useRecaptchaHeaders();
   const [plans, setPlans] = useState([]);
   // The organization's active workspaces, each with its active projects: what
   // a smaller plan has to fit. Null until read, or when it can't be.
   const [usage, setUsage] = useState(null);
   const [isLoading, setIsLoading] = useState(false);
   const [isRequestingEnterprise, setIsRequestingEnterprise] = useState(false);
   // Asking for Enterprise: the dialog it is asked in, and what it tells our team.
   const [isEnterpriseDialogOpen, setIsEnterpriseDialogOpen] = useState(false);
   const [enterpriseNeeds, setEnterpriseNeeds] = useState("");
   const [openPackageModal, setOpenPackageModal] = useState(false);
   // The plan chosen, and what choosing it does (planChanges.js).
   const [selectedPlanToChange, setSelectedPlanToChange] = useState({ plan: null, change: null });
   const [isPlanChanging, setIsPlanChanging] = useState(false);
   const [isYearly, setIsYearly] = useState(!isMonthly);
   const [promoCode, setPromoCode] = useState("");
   const billingStatus = useBillingStore((state) =>
      billingManaged ? state.statuses[AuthService.getOrganizationId()] : null
   );
   // Enterprise, on the new flow (components/Billing/enterprise.js): the terms
   // in force while the organization is on it, terms our team has offered it,
   // and a request still waiting for terms. Its price and limits are its terms,
   // never the packet's.
   const enterpriseNow = enterprisePlanOf(currentEnterpriseTerms(billingStatus));
   const enterpriseOffer = enterprisePlanOf(billingStatus?.enterpriseOffer);
   const isEnterpriseRequested = isEnterpriseRequestOpen(billingStatus);
   // What the subscription is on now. After a change the page reads it again,
   // and this component comes back with it. The subscription read prices the
   // Enterprise packet, not the terms, so on Enterprise the terms say.
   const currentPrice = enterpriseNow ? getPlanPrice(enterpriseNow, !isMonthly) : price;
   const selectedPlan = { name: planName, price: currentPrice };
   const rulesOn = isPlanDowngradesEnabled();
   // A trial is changed at once, on either flow: nothing has been paid for a
   // period to run out, and both keep the trial's end.
   const isTrialing = subscriptionStatus === SubscriptionStatus.TRIALING;
   // An annual plan with more than its current month left to run.
   const commitment = billingManaged
      ? billingStatus
      : oldFlowCommitment({ subscriptionStatus, subscriptionEndsAt, commitmentEnd });
   const isCommitted = isCommittedPastThisPeriod(commitment);
   // A change waiting for the end of the contract. Choosing another plan
   // replaces it; the notice above the plans drops it.
   const pendingSource = billingManaged
      ? billingStatus?.pendingPlanChange
      : subscriptionPendingPlanChange;
   const pendingPlanChange = rulesOn && pendingSource?.packetId ? pendingSource : null;
   // When a cancellation already asked for ends the subscription: from the
   // status on the new flow, from the subscription read on the old one.
   // Scheduling a change takes it back; an upgrade leaves it be.
   const cancellationEnd = billingManaged
      ? scheduledEndOf(billingStatus)
      : cancelAt ?? null;

   // Every account is offered every plan and the Enterprise package, whichever
   // level it is at: the level follows the plan (pages/auth/register/
   // accountLevel.js). The account's own level only decides what the move to
   // Customer Feedback warns about.
   const accountLevel = getAccountLevel(getCurrentOrganizationLevel());
   const isBusinessLevel = accountLevel === AccountLevel.Business;
   const planOptions = getPlanChangeOptions(plans);
   // The packet in force, among those offered, or the Enterprise terms in force.
   const currentPlan =
      enterpriseNow ??
      plans.find((plan) => plan.id === billingStatus?.packetId) ??
      plans.find((plan) => plan.name === planName) ??
      null;

   useEffect(() => {
      setIsLoading(true);

      // The packets are asked for by level — 0 for an organization's, 1 for a
      // business's — and both are offered. An API that ignores the level
      // answers both with the same packets, so each is kept once, and the
      // lists are filtered by level again above.
      const levels = [OrganizationLevel.Organization, OrganizationLevel.Business];

      Promise.allSettled(
         levels.map((organizationLevel) =>
            httpService.get(
               `/Account/getPackets?${new URLSearchParams({ organizationLevel }).toString()}`
            )
         )
      )
         .then((answers) => {
            const packets = new Map();
            answers.forEach((answer) => {
               if (answer.status !== "fulfilled") return;
               (answer.value?.data ?? []).forEach((packet) => {
                  if (!packets.has(packet.id)) packets.set(packet.id, packet);
               });
            });
            setPlans([...packets.values()]);
         })
         .finally(() => {
            setIsLoading(false);
         });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   useEffect(() => {
      if (rulesOn) readPlanUsage().then(setUsage);
   }, [rulesOn]);

   // What choosing a plan, on the period the switch shows, does.
   const changeTo = (plan) => {
      if (rulesOn && !currentPlan) {
         return {
            kind: PlanChangeKind.UNAVAILABLE,
            reason:
               planName && !isSelfServicePacket(planName)
                  ? UnavailableReason.UNSUPPORTED
                  : UnavailableReason.MISSING_DATA
         };
      }
      if (!rulesOn) {
         const isCurrent =
            selectedPlan.name === plan.name &&
            (isYearly
               ? selectedPlan.price === plan.annuallyPrice
               : selectedPlan.price === plan.monthlyPrice);
         return {
            kind: isCurrent
               ? PlanChangeKind.CURRENT
               : subscriptionStatus === SubscriptionStatus.PAUSED
               ? PlanChangeKind.PAUSED
               : PlanChangeKind.NOW
         };
      }
      return resolvePlanChange({
         current: { plan: currentPlan, isMonthly },
         target: { plan, isMonthly: !isYearly },
         subscription: {
            ...commitment,
            currentPeriodStart: subscriptionStartsAt,
            isCancelled: Boolean(cancellationEnd)
         },
         usage
      });
   };

   // A change made now, as the backend makes it today (`create_prorations`):
   // nothing charged now, and the difference for the days left in the period
   // on the next invoice, worked out for this change. Without the period's
   // dates it says how it works instead. A plan still on an old price billed
   // once a year is billed at once by Stripe, for a new monthly period.
   const describeChangeNow = (plan) => {
      const newPrice = getPlanPrice(plan, isYearly);
      const periodEnd = subscriptionEndsAt ?? commitment?.currentPeriodEnd;
      const nextBilling = formatBillingDate(periodEnd);
      const terms = isYearly ? ", on a 12-month commitment" : "";
      const taxNote = "These are estimates, before tax and any promo code.";

      if (isYearlyPeriod(subscriptionStartsAt, periodEnd)) {
         return [OLD_YEARLY_CHANGE, `From then you pay ${formatPlanPrice(plan, isYearly)} a month${terms}.`, taxNote];
      }

      const estimate = estimateProration({
         currentPrice,
         newPrice,
         periodStart: subscriptionStartsAt,
         periodEnd
      });
      if (!estimate || !nextBilling) {
         return [
            "The plan changes now, and you pay nothing today.",
            "Your next invoice adds the new plan's price for the days left in this period, less the unused part of your current plan. If the new plan is cheaper, that difference is taken off the invoice instead.",
            taxNote
         ];
      }

      const newName = displayName(plan.name);
      const currentName = displayName(planName);
      const daysLeft = `the ${estimate.daysLeft} ${
         estimate.daysLeft === 1 ? "day" : "days"
      } left until ${nextBilling}`;
      const difference =
         estimate.difference > 0
            ? `For ${daysLeft}, your next invoice adds about ${formatAmount(
                 estimate.difference
              )}: the ${newName} plan for those days (${formatAmount(
                 estimate.newPart
              )}), less the unused part of your ${currentName} plan (${formatAmount(
                 estimate.unusedPart
              )}).`
            : estimate.difference < 0
            ? `For ${daysLeft}, your next invoice takes off about ${formatAmount(
                 -estimate.difference
              )}: the unused part of your ${currentName} plan (${formatAmount(
                 estimate.unusedPart
              )}), less the ${newName} plan for those days (${formatAmount(estimate.newPart)}).`
            : `Nothing is added to your next invoice for ${daysLeft}.`;
      // Only the plan's part of that invoice: tax, discounts, credits and
      // anything else on it come on top.
      const planPart =
         estimate.nextInvoice >= 0
            ? `The plan's part of that invoice is estimated at about ${formatAmount(
                 estimate.nextInvoice
              )}.`
            : `The plan's part of that invoice is estimated at $0.00, with about ${formatAmount(
                 -estimate.nextInvoice
              )} left over as credit.`;

      return [
         "The plan changes now, and you pay nothing today.",
         difference,
         `From ${nextBilling} you pay ${formatPlanPrice(plan, isYearly)} a month${terms}. ${planPart}`,
         taxNote
      ];
   };

   // What the dialog says will happen, before anything is confirmed: a line,
   // or a few.
   const describeWhatHappens = ({ plan, change }) => {
      if (!plan || !change) return null;
      const newPrice = formatPlanPrice(plan, isYearly);

      const cancellationEndsOn = formatBillingDate(cancellationEnd);

      switch (change.kind) {
         case PlanChangeKind.TRIAL:
            return TRIAL_CHANGE;
         case PlanChangeKind.PAUSED:
            return PAUSED_CHANGE;
         case PlanChangeKind.UPGRADE: {
            // The year is paid for, so Stripe prorates it instead of a monthly difference.
            if (change.oldYearlyPrice) {
               return [
                  OLD_YEARLY_CHANGE,
                  `From then you pay ${newPrice} a month${isYearly ? ", on a 12-month commitment" : ""}.`
               ];
            }
            const nextBilling = formatBillingDate(commitment?.currentPeriodEnd);
            const commitmentEndsOn = formatBillingDate(commitment?.commitmentEnd);
            const terms = !isYearly
               ? ""
               : isMonthly
               ? ", on a 12-month commitment that started with this month"
               : `, until your 12-month commitment ends${
                    commitmentEndsOn ? ` on ${commitmentEndsOn}` : ""
                 }`;
            // Not worked out between prices in two currencies (Enterprise terms
            // can be in another).
            const chargeToday = formatAmountBetween(change.chargeToday, currentPlan, plan);
            return [
               `The plan changes now. ${
                  chargeToday
                     ? `You're charged ${chargeToday} today (before tax), the difference for this month.`
                     : "You're charged the difference for this month today (before tax)."
               } From ${nextBilling ?? "your next billing date"}, you pay ${newPrice} a month${terms}.`,
               // The backend charges today's difference in full.
               "A promo code applies from your next billing date, not to today's charge.",
               ...(cancellationEndsOn
                  ? [`Your subscription still ends on ${cancellationEndsOn}, as you asked.`]
                  : [])
            ];
         }
         case PlanChangeKind.SCHEDULED: {
            const date = formatBillingDate(change.effectiveAt);
            const contract = isMonthly
               ? "the month you've paid for ends"
               : "your 12-month commitment ends";
            return [
               `Your plan changes ${date ? `on ${date}, ` : ""}when ${contract}. Until then, nothing changes. From then you pay ${newPrice} a month${
                  isYearly ? ", on a 12-month commitment" : ""
               }.`,
               // The backend takes a cancellation back when a change is scheduled.
               ...(cancellationEndsOn
                  ? ["Choosing it also takes back your cancellation: your subscription carries on."]
                  : [])
            ];
         }
         default:
            return isTrialing ? TRIAL_CHANGE : describeChangeNow(plan);
      }
   };

   // What the notice says once it is done.
   // The plan with its period: "the Premium annual plan", so a move between the
   // periods of one packet doesn't read as no move at all.
   const describeChangeMade = (plan, change, effectiveAt) => {
      const target = `the ${plan.name} ${isYearly ? "annual" : "monthly"} plan`;
      return change.kind === PlanChangeKind.SCHEDULED
         ? `You move to ${target} ${
              effectiveAt ? `on ${effectiveAt}` : "when your contract ends"
           }. Until then, nothing changes.`
         : change.kind === PlanChangeKind.PAUSED
         ? `You move to ${target} once you add a card.`
         : `You're now on ${target}.`;
   };

   // Whether what the server answered, or read back, holds the plan asked for,
   // now or waiting for the end of the contract (planChanges.js, planHeldIn).
   // A 200 can carry neither (a scheduling Stripe treated as a repeat), and
   // then the page says so rather than "successfully".
   const holdsChange = (answer, plan) => planHeldIn(answer, plan, !isYearly) !== null;
   const changeAsMade = (answer, plan, change) => changeAsAnswered(answer, plan, !isYearly, change);

   const NOT_CHANGED =
      "Your plan hasn't changed: the change didn't go through. Please try again later, or contact us if it keeps happening.";

   const changeMadeTitle = (change) =>
      change.kind === PlanChangeKind.SCHEDULED
         ? "Plan change scheduled successfully"
         : change.kind === PlanChangeKind.PAUSED
         ? "Plan change saved successfully"
         : "Plan changed successfully";

   const handlePlanSelection = (plan, change) => {
      // Only our team makes this change: said at once, with no dialog that
      // would promise what the backend then refuses.
      if (change.kind === PlanChangeKind.NEEDS_SUPPORT) {
         announceDowngradeNeedsSupport();
         return;
      }
      if (!isPlanChangeOffered(change.kind) || subscriptionStatus === "canceled") return;
      setOpenPackageModal(true);
      setPromoCode("");
      setSelectedPlanToChange({ plan, change });
   };

   // A smaller plan refused because the organization doesn't fit it after all:
   // what it holds changed since the page read it. The server's own words are
   // shown as they are. Read again, the plan's button says what to archive.
   const explainOverLimits = async (plan, serverMessage) => {
      setOpenPackageModal(false);
      if (rulesOn) setUsage(await readPlanUsage());
      notification.warning({
         message: "Heads up!",
         description: serverMessage || `${describeOverLimits(plan)} Your plan hasn't changed.`
      });
   };

   // The new flow. The plan and the billing period change together, now or
   // when the contract ends, and the page is read again afterwards so it shows
   // what the server settled on rather than what was asked for. A change for
   // the end of the contract only waits: the server answers it as
   // `pendingPlanChange`, and the plan and its limits apply then.
   const changePlanThroughBilling = async () => {
      const { plan, change } = selectedPlanToChange;
      const when =
         change.kind === PlanChangeKind.SCHEDULED
            ? PlanChangeTiming.PERIOD_END
            : PlanChangeTiming.NOW;
      const organizationId = AuthService.getOrganizationId();

      setIsPlanChanging(true);

      try {
         const answer = await billingApi.changePlan(
            {
               organizationId,
               packetId: plan.id,
               isMonthlySubscription: !isYearly,
               when,
               promoCode
            },
            { rememberStatus: false }
         );
         // A plan of the other kind of account changes the level, which the
         // server sets from the plan and doesn't send back. The organization is
         // read again for it before the status is kept, since keeping the
         // status is what has the app render its menu and redirects again.
         const status = await rememberNewSubscription(organizationId, answer);

         setOpenPackageModal(false);

         // Paying the difference now can need the bank's check. The plan only
         // changes once it has gone through, which the server hears from Stripe.
         // A refused check leaves nothing owed once the difference invoice is
         // voided, so whether the plan moved is what tells it from a payment.
         if (when === PlanChangeTiming.NOW && status?.requiresAction) {
            const settled = await settlePlanChangePayment(status, organizationId, (answer) =>
               holdsChange(answer, plan)
            );
            if (settled.error) {
               notification.error({
                  message: "Oops! A problem has occurred!",
                  description: `${
                     settled.error.message ?? describeBillingError({})
                  } Your plan hasn't changed.`
               });
               onPlanChanged?.();
               return;
            }
            if (settled.needsHostedPage && settled.status?.hostedInvoiceUrl) {
               announceInvoiceToConfirm(settled.status.hostedInvoiceUrl);
               onPlanChanged?.();
               return;
            }
         }

         // Rules on, the answer has to hold the change; off, the backend before
         // them answers the plan it changed the same way.
         // A change kept for a paused subscription isn't reported in the answer
         // (the old flow skips this check for it too).
         if (
            rulesOn &&
            change.kind !== PlanChangeKind.PAUSED &&
            !status?.requiresAction &&
            !holdsChange(status, plan)
         ) {
            notification.warning({ message: "Heads up!", description: NOT_CHANGED });
            onPlanChanged?.();
            return;
         }

         // Said as the answer holds it: now, or waiting for the contract end.
         const made =
            rulesOn && change.kind !== PlanChangeKind.PAUSED && !status?.requiresAction
               ? changeAsMade(status, plan, change)
               : change;
         notification.success({
            message: changeMadeTitle(made),
            description: describeChangeMade(
               plan,
               made,
               formatBillingDate(status?.pendingPlanChange?.effectiveAt ?? change.effectiveAt)
            )
         });
         onPlanChanged?.();
      } catch (failure) {
         // An annual plan asked to go monthly before its commitment's last
         // month: the status held didn't say so yet. Read again, it does, and
         // the monthly plans are no longer offered. The server's words name
         // the commitment's end and what it allows.
         if (failure?.code === BillingCode.COMMITMENT_ACTIVE) {
            setOpenPackageModal(false);
            setIsYearly(true);
            await billingApi.getBillingStatus(organizationId).catch(() => null);
            notification.warning({ message: "Heads up!", description: describeBillingError(failure) });
            return;
         }

         // A change for the end of the contract that Stripe didn't leave
         // scheduled (502): nothing changed, and it can be picked again. Read
         // again, so the page shows no change waiting — the same as a 200 that
         // holds none (holdsChange above).
         if (failure?.code === BillingCode.PLAN_CHANGE_NOT_SCHEDULED) {
            setOpenPackageModal(false);
            notification.warning({ message: "Heads up!", description: describeBillingError(failure) });
            onPlanChanged?.();
            return;
         }

         if (failure?.code === BillingCode.PLAN_LIMIT_EXCEEDED) {
            await explainOverLimits(plan, failure.serverMessage);
            onPlanChanged?.();
            return;
         }

         // The payment owed is settled first: a scheduled change while it is
         // owed, and now also an upgrade on a subscription that is incomplete
         // or unpaid. Read again, so nothing shows as waiting and the payment
         // notice is there to settle it from.
         if (failure?.code === BillingCode.PAYMENT_DUE) {
            setOpenPackageModal(false);
            announcePaymentDue(true);
            onPlanChanged?.();
            return;
         }

         if (failure?.code === BillingCode.DOWNGRADE_REQUIRES_SUPPORT) {
            setOpenPackageModal(false);
            announceDowngradeNeedsSupport();
            onPlanChanged?.();
            return;
         }

         // A previous annual plan ended unpaid and still owes money. The status
         // the refusal carries, debt and all, is kept: it holds the organization,
         // and the routes take it to the billing page to settle the balance.
         if (failure?.code === BillingCode.OUTSTANDING_DEBT) {
            setOpenPackageModal(false);
            if (failure.status) await rememberNewSubscription(organizationId, failure.status);
            notification.warning({ message: "Heads up!", description: describeBillingError(failure) });
            return;
         }

         // An upgrade's difference declined by the bank: 409, the bank's reason
         // in the message, the plan unchanged. Read again all the same.
         if (failure?.code === BillingCode.PAYMENT_FAILED) {
            setOpenPackageModal(false);
            notification.error({
               message: "Oops! A problem has occurred!",
               description: describeBillingError(failure)
            });
            onPlanChanged?.();
            return;
         }

         // Stripe no longer has the subscription to change. Read again, the
         // status holds the organization for a plan, and the routes take it to
         // the billing page to pick one, so the move is explained rather than
         // reported as an error.
         if (await recoverFromNoSubscription(failure, organizationId)) {
            setOpenPackageModal(false);
            notification.warning({
               message: "Heads up!",
               description: NO_SUBSCRIPTION_LEFT_MESSAGE
            });
            return;
         }

         // Anything else, STRIPE_ERROR among them, in the server's words. The
         // page reads what the server has rather than assuming the old plan: an
         // upgrade whose payment went through before the switch failed is
         // completed by the webhook a few seconds later, so it is read once more
         // after that.
         setOpenPackageModal(false);
         notification.error({
            message: "Oops! A problem has occurred!",
            description: describeBillingError(failure)
         });
         onPlanChanged?.();
         if (when === PlanChangeTiming.NOW) {
            setTimeout(() => {
               billingApi.getBillingStatus(organizationId).catch(() => null);
            }, UPGRADE_COMPLETION_READ_MS);
         }
      } finally {
         setIsPlanChanging(false);
      }
   };

   const changePlan = async () => {
      if (billingManaged) {
         changePlanThroughBilling();
         return;
      }

      const { plan } = selectedPlanToChange;
      let { change } = selectedPlanToChange;
      setIsPlanChanging(true);
      const params = new URLSearchParams({
         stripeSubscriptionId: subscriptionId,
         newPacketId: plan.id,
         // Always said: left out, the server picks the period itself.
         isMonthlySubscription: !isYearly,
         // Always false: the server decides when by the rules, and the backend
         // before them ends a monthly plan with the period when asked for it.
         updatePlanAfterPeriodEnds: false,
         promoCode
      });
      // Deprecated with the new flow, rate limited, and refused without the
      // reCAPTCHA header — so when no token can be had, nothing is sent.
      let headers;
      try {
         headers = await recaptchaHeaders();
      } catch (unavailable) {
         notification.error({
            message: "Oops! A problem has occurred!",
            description: unavailable.message
         });
         setIsPlanChanging(false);
         return;
      }

      httpService
         .get(`/Payment/updateSubscriptionPlan?${params.toString()}`, undefined, undefined, {
            headers
         })
         .then(async (response) => {
            // A subscription or plan that isn't found is a 404 now, which
            // rejects; the backend before it answered a success with
            // statusCode 204.
            if (typeof response?.statusCode === "number" && response.statusCode !== 200) {
               throw response;
            }
            // Its answer is only `true`: the subscription read back has to hold
            // the change. A change kept for a paused subscription isn't in it.
            if (rulesOn && change.kind !== PlanChangeKind.PAUSED) {
               const subscription = await httpService
                  .get(
                     `/Payment/getStripeSubscriptionByOrganization?organizationId=${AuthService.getOrganizationId()}`
                  )
                  .then((answer) => answer?.data)
                  .catch(() => null);
               if (subscription && !holdsChange(subscription, plan)) {
                  notification.warning({ message: "Heads up!", description: NOT_CHANGED });
                  onPlanChanged?.();
                  return;
               }
               if (subscription) change = changeAsMade(subscription, plan, change);
            }
            notification.success({
               message: changeMadeTitle(change),
               description:
                  change.kind === PlanChangeKind.SCHEDULED
                     ? describeChangeMade(plan, change, formatBillingDate(change.effectiveAt))
                     : response?.message || describeChangeMade(plan, change)
            });
            // The server keeps the period of the price Stripe now bills, and any
            // change waiting for the end of the contract, so the page reads back
            // what it settled on.
            onPlanChanged?.();
         })
         .catch(async (failure) => {
            const refusal =
               failure?.response?.data ??
               (typeof failure?.statusCode === "number" ? failure : null);
            const refusalError = { code: refusal?.code, serverMessage: refusal?.message };

            // An annual plan asked to go monthly before its commitment's last
            // month: the subscription read the page has didn't say so yet. The
            // page reads it again, so the monthly plans are no longer offered;
            // the server's words name the commitment's end and what it allows.
            // A change for the end of the contract that Stripe didn't leave
            // scheduled (502) is read back the same way: nothing changed, and
            // it can be picked again.
            if (
               refusal?.code === BillingCode.COMMITMENT_ACTIVE ||
               refusal?.code === BillingCode.PLAN_CHANGE_NOT_SCHEDULED
            ) {
               notification.warning({
                  message: "Heads up!",
                  description: describeBillingError(refusalError)
               });
               onPlanChanged?.();
               return;
            }

            if (refusal?.code === BillingCode.PLAN_LIMIT_EXCEEDED) {
               await explainOverLimits(plan, refusal.message);
               onPlanChanged?.();
               return;
            }

            // The payment owed is settled first; the page reads back that
            // nothing is waiting.
            if (refusal?.code === BillingCode.PAYMENT_DUE) {
               announcePaymentDue(false);
               onPlanChanged?.();
               return;
            }

            if (refusal?.code === BillingCode.DOWNGRADE_REQUIRES_SUPPORT) {
               announceDowngradeNeedsSupport();
               onPlanChanged?.();
               return;
            }

            // An upgrade's difference is charged at once, and the bank wants to
            // confirm it. The old flow has no card form to do that in, so it is
            // confirmed on Stripe's invoice page; the plan changes once paid.
            if (
               refusal?.code === BillingCode.PAYMENT_ACTION_REQUIRED &&
               refusal?.data?.hostedInvoiceUrl
            ) {
               announceInvoiceToConfirm(refusal.data.hostedInvoiceUrl);
               onPlanChanged?.();
               return;
            }

            // Anything else — a declined payment in the bank's words,
            // NO_SUBSCRIPTION, STRIPE_ERROR — in the server's words, and the
            // page reads back what the server has rather than assuming it.
            notification.error({
               message: "Oops! A problem has occurred!",
               description:
                  (typeof refusal?.message === "string" && refusal.message) ||
                  (Array.isArray(refusal?.errors) && refusal.errors[0]) ||
                  "We couldn't change your plan. Please try again."
            });
            onPlanChanged?.();
         })
         .finally(() => {
            setOpenPackageModal(false);
            setIsPlanChanging(false);
         });
   };

   // Enterprise asked for while this plan runs, with no terms agreed yet:
   // change-plan with its id only records the request, and this plan carries
   // on. With terms on offer the same call moves onto them, so the status is
   // read first, and terms offered since the page read it are shown instead.
   // Once recorded, our team is sent the request with what the dialog was told
   // the plan needs (components/Billing/enterpriseRequest.js).
   const requestEnterprise = async () => {
      if (isRequestingEnterprise) return;
      const organizationId = AuthService.getOrganizationId();
      setIsRequestingEnterprise(true);

      try {
         if (await hasEnterpriseTermsNow(organizationId)) {
            setIsEnterpriseDialogOpen(false);
            notification.warning({
               message: "Heads up!",
               description: "Your Enterprise terms are ready. Look them over below before you choose them."
            });
            return;
         }

         const status = await billingApi.changePlan({
            organizationId,
            packetId: ENTERPRISE_PACKET_ID,
            isMonthlySubscription: !isYearly,
            when: PlanChangeTiming.NOW
         });
         setIsEnterpriseDialogOpen(false);
         if (status?.enterpriseRequested) {
            const messageSent = await notifyEnterpriseRequest({
               ...signedInRequester(),
               source: EnterpriseRequestSource.SUBSCRIPTION_PAGE,
               needs: enterpriseNeeds
            });
            notification.success({
               message: "Enterprise requested successfully",
               description: messageSent
                  ? "We've sent your request to our team and emailed you a confirmation. Your current plan carries on until you take up the terms they offer."
                  : "Our team will contact you to agree its terms. Your current plan carries on until you take them up."
            });
            if (!messageSent) {
               notification.warning({ message: "Heads up!", description: ENTERPRISE_MESSAGE_NOT_SENT });
            }
         } else {
            notification.warning({ message: "Heads up!", description: NOT_CHANGED });
         }
         onPlanChanged?.();
      } catch (failure) {
         notification.error({
            message: "Oops! A problem has occurred!",
            description: describeBillingError(failure)
         });
      } finally {
         setIsRequestingEnterprise(false);
      }
   };

   const handlePricingToggle = (e) => {
      setIsYearly(e.target.value === "yearly");
      localStorage.setItem("isYearly", e.target.value === "yearly");
   };

   const onChange = (value) => {
      setPromoCode(value);
   };

   // What a plan's card offers: whether it is the plan in force, and its
   // button — "Current plan", "Choose this plan" or the date a change waiting
   // for it starts — with why it can't be chosen, when it can't, in a tooltip.
   const planChoice = (plan) => {
      const change = changeTo(plan);
      const isSelected = change.kind === PlanChangeKind.CURRENT;
      // Why the plan can't be chosen, said over its button.
      const blockedReason =
         change.kind === PlanChangeKind.OVER_LIMITS
            ? describeOverLimits(plan)
            : change.kind === PlanChangeKind.COMMITTED
            ? describeCommitment(commitment?.commitmentEnd)
            : change.kind === PlanChangeKind.UNAVAILABLE
            ? UNAVAILABLE_REASONS[change.reason]
            : null;
      // The plan a change waiting for the end of the contract moves to. One to
      // Enterprise is to the terms on offer, or with none, to the other period
      // of the terms in force: the change names the packet alone.
      const isPending =
         pendingPlanChange?.packetId === plan.id &&
         pendingPlanChange.isMonthly === !isYearly &&
         (!plan.terms || plan.termsId === (enterpriseOffer ?? enterpriseNow)?.termsId);
      const pendingFrom = isPending ? formatBillingDate(pendingPlanChange.effectiveAt) : null;
      const button = (
         <Button
            type="default"
            className={`defaultButton ${isSelected ? "currentPlan" : "choosePlan"}`}
            disabled={isSelected || isPending || Boolean(blockedReason)}
            onClick={() => handlePlanSelection(plan, change)}
         >
            {isSelected
               ? "Current plan"
               : isPending
               ? pendingFrom
                  ? `Starts ${pendingFrom}`
                  : "Scheduled"
               : "Choose this plan"}
         </Button>
      );
      return {
         isSelected,
         // A disabled button takes no hover, so the tooltip goes on a wrapper.
         button: blockedReason ? (
            <Tooltip title={blockedReason}>
               <span className="blockedPlan">{button}</span>
            </Tooltip>
         ) : (
            button
         )
      };
   };

   // Enterprise terms on a card like the Enterprise one: the terms in force,
   // as the current plan, or new ones our team offered. The price follows the
   // switch, in the terms' currency, with the other period's beside it.
   const renderEnterpriseTerms = (plan, title) => {
      const { isSelected, button } = planChoice(plan);
      const { terms } = plan;
      return (
         <div className={`subscriptionPlans enterprisePlan ${isSelected ? "selected" : ""}`}>
            <div className="planTitle">
               <span
                  style={{
                     display: "flex",
                     alignItems: "center",
                     justifyContent: "center",
                     flexWrap: "wrap"
                  }}
               >
                  <StarRewardRate />
                  {title}
               </span>
            </div>
            <div className="payment">
               <span>{terms.currency.toUpperCase()}</span>{" "}
               <span className="annuallyPrice">{formatEnterprisePrice(terms, isYearly)}</span>
               <span style={{ color: "#999999" }}> per month</span>
               {isYearly && <span className="billingTerms">{ENTERPRISE_ANNUAL_NOTE}</span>}
               <span className="billingTerms">
                  {isYearly
                     ? `Or ${formatEnterprisePrice(terms, false)} a month on the monthly plan`
                     : `Or ${formatEnterprisePrice(terms, true)} a month on the annual plan`}
               </span>
            </div>
            <div className="content">
               <p
                  style={{
                     color: isSelected ? "#DE8344" : "#9BACEB",
                     fontFamily: "Poppins",
                     fontSize: "14px",
                     fontWeight: "600"
                  }}
               >
                  This plan includes:
               </p>
               <p>
                  <AiOutlineCheckCircle />
                  &nbsp;
                  <b>{terms.workspaceLimit ?? "Unlimited"} </b>
                  workspaces
               </p>
               <p>
                  <AiOutlineCheckCircle />
                  &nbsp;
                  <b>{terms.projectLimit ?? "Unlimited"} </b>
                  projects/workspace
               </p>
               {/* The Enterprise packet caps neither. */}
               <p>
                  <AiOutlineCheckCircle />
                  &nbsp;
                  <b>Unlimited</b> users/workspace
               </p>
               <p>
                  <AiOutlineCheckCircle />
                  &nbsp;
                  <b>Unlimited</b> data storage
               </p>
            </div>
            <div className="enterpriseActions">{button}</div>
         </div>
      );
   };

   return (
      <>
         {isLoading ? (
            <Spin
               style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: "50px"
               }}
            ></Spin>
         ) : (
            <div className="subscriptionPlanBoxes">
               <Row>
                  <Col xs={24} sm={18} md={16} lg={16} xl={12}>
                     <h6>Current Subscription</h6>
                  </Col>
                  <Col className="columnPosition" xs={24} sm={18} md={16} lg={16} xl={12}>
                     <Radio.Group
                        onChange={handlePricingToggle}
                        value={isYearly ? "yearly" : "monthly"}
                     >
                        <Radio value="yearly">Annual plan</Radio>
                        <Radio value="monthly" disabled={isCommitted}>
                           Monthly plan
                        </Radio>
                     </Radio.Group>
                  </Col>
               </Row>
               {isCommitted && (
                  <Alert
                     className="mt-8"
                     type="info"
                     showIcon
                     message={describeCommitment(commitment.commitmentEnd)}
                  />
               )}
               <Row
                  direction="row"
                  className="mt-8 mb-8 planRow"
                  justify={planOptions.length < 3 ? "center" : "space-between"}
                  gutter={[24, 24]}
               >
                  {/* Cheapest first: the plans come the other way round. */}
                  {[...planOptions].reverse().map((plan, i) => {
                     const { isSelected, button } = planChoice(plan);
                     return (
                        <Col xs={24} sm={18} md={12} lg={16} xl={8} key={plan.id}>
                           <div className={`subscriptionPlans ${isSelected ? "selected" : ""}`}>
                              <div className="planTitle">
                                 {/* No trial line. Whoever changes plan here has
                                     already had the organization's one free
                                     trial, and the packet's `trialDays` is what
                                     a first subscription gets, not a plan
                                     change. */}
                                 <span
                                    style={{
                                       display: "flex",
                                       alignItems: "center",
                                       justifyContent: "center",
                                       flexWrap: "wrap"
                                    }}
                                 >
                                    {plan.name === "Standard" ? (
                                       <StarRewardBadge />
                                    ) : (
                                       <StarRewardRate />
                                    )}
                                    <b style={{ marginLeft: "5px" }}>
                                       {plan.name === "Standard" ? "Starter" : plan.name}{" "}
                                    </b>
                                    &nbsp; package
                                 </span>
                              </div>
                              <div className="payment">
                                 <span>USD</span>{" "}
                                 <span className="annuallyPrice">
                                    {formatPrice(getPlanPrice(plan, isYearly))}
                                 </span>
                                 <span style={{ color: "#999999" }}> per month</span>
                                 {isYearly && (
                                    <span className="billingTerms">{ANNUAL_BILLING_NOTE}</span>
                                 )}
                              </div>
                              <div className="content">
                                 <p
                                    style={{
                                       color: isSelected ? "#DE8344" : "#9BACEB",
                                       fontFamily: "Poppins",
                                       fontSize: "14px",
                                       fontWeight: "600"
                                    }}
                                 >
                                    This plan includes:
                                 </p>
                                 <p>
                                    <AiOutlineCheckCircle />
                                    &nbsp;
                                    <b>
                                       {plan.workspaceLimit != 0
                                          ? plan.workspaceLimit
                                          : "Unlimited"}{" "}
                                    </b>
                                    workspaces
                                 </p>
                                 {/* A smaller plan has to fit: the projects over it are archived first. */}
                                 <p>
                                    <AiOutlineCheckCircle />
                                    &nbsp;
                                    <b>
                                       {plan.projectDashboardLimit > 0
                                          ? plan.projectDashboardLimit
                                          : "Unlimited"}{" "}
                                    </b>
                                    projects/workspace
                                 </p>
                                 <p>
                                    <AiOutlineCheckCircle />
                                    &nbsp;
                                    <b>
                                       {plan.membersLimit != 0 ? plan.membersLimit : "Unlimited"}{" "}
                                    </b>
                                    users/workspace
                                 </p>
                                 <p>
                                    <AiOutlineCheckCircle />
                                    &nbsp;
                                    {plan.databaseLimit != 0 ? (
                                       <>
                                          <b>{plan?.databaseLimit} GB</b> data storage limit
                                       </>
                                    ) : (
                                       <>
                                          <b>Unlimited</b> data storage
                                       </>
                                    )}
                                 </p>
                              </div>
                              <PlanDetail planData={plan} isYearly={isYearly} />
                              {button}
                           </div>
                        </Col>
                     );
                  })}
                  {openPackageModal && (
                     <BasicModal
                        isModalOpen={openPackageModal}
                        handleCancel={() => setOpenPackageModal(false)}
                        footerButtonCustomStyle
                     >
                        <div className="mt-5 mb-8">
                           <h4>How would you like to update your subscription plan?</h4>
                        </div>
                        {/* An organization moving to Customer Feedback becomes a
                            business account, which sees a slice of the platform
                            (base/services/accountLevel.service.js). The rest is
                            only hidden: the level decides what the app shows. */}
                        {!isBusinessLevel &&
                           getPlanAccountLevel(selectedPlanToChange.plan) ===
                              AccountLevel.Business && (
                              <Alert
                                 className="mb-8"
                                 type="info"
                                 showIcon
                                 message="Customer Feedback keeps customer feedback surveys, their automated campaigns, project dashboards and survey analytics. Standard surveys, templates, audiences and assignments are hidden until you move back to an organization plan. Nothing is deleted."
                              />
                           )}
                        {pendingPlanChange && (
                           <Alert
                              className="mb-8"
                              type="info"
                              showIcon
                              message={`This replaces the change to the ${
                                 pendingPlanChange.packetName ?? "new"
                              } plan you scheduled${
                                 formatBillingDate(pendingPlanChange.effectiveAt)
                                    ? ` for ${formatBillingDate(pendingPlanChange.effectiveAt)}`
                                    : ""
                              }.`}
                           />
                        )}
                        {/* When it happens, and what it costs, follow from the
                            plans (planChanges.js): nothing to choose. */}
                        {[]
                           .concat(describeWhatHappens(selectedPlanToChange) ?? [])
                           .map((line) => (
                              <p className="promoCodeDescription" key={line}>
                                 {line}
                              </p>
                           ))}
                        <p className="promoCodeDescription">
                           If you have received a promo code from our team, please enter it
                           below to apply your exclusive discount to the new subscription
                           plan.
                        </p>

                        <div className="promoCodeInput">
                           <Input
                              value={promoCode}
                              onChange={(e) => onChange(e.target.value)}
                              placeholder={"Enter promo code"}
                              className="promoCodeInput__field"
                           />
                        </div>

                        {billingManaged && (
                           <PromoCodePreview
                              organizationId={AuthService.getOrganizationId()}
                              packetId={selectedPlanToChange.plan?.id}
                              isMonthly={!isYearly}
                              promoCode={promoCode}
                              trialing={subscriptionStatus === SubscriptionStatus.TRIALING}
                           />
                        )}

                        <Row gutter={16} className="mt-8" justify="center" align="middle">
                           <Col>
                              <Button
                                 className="cancel"
                                 onClick={() => setOpenPackageModal(false)}
                                 disabled={isPlanChanging}
                              >
                                 Cancel
                              </Button>
                           </Col>
                           <Col>
                              <Button
                                 onClick={changePlan}
                                 className="primaryButton"
                                 loading={isPlanChanging}
                              >
                                 Continue
                              </Button>
                           </Col>
                        </Row>
                     </BasicModal>
                  )}
               </Row>
               {/* Enterprise, offered to every account. Under the priced plans
                   rather than beside them: a strip across the whole row on a
                   wide screen, a card like the others below. On the new flow
                   it is the organization's own terms (components/Billing/
                   enterprise.js): the ones it is on, as its current plan, and
                   new ones our team offered, chosen like any plan. With
                   neither, it is asked for here, and says so once it is. The
                   old flow keeps the contact card. */}
               <Row className="mb-8" gutter={[24, 24]}>
                  {enterpriseNow && (
                     <Col xs={24} sm={18} md={12} lg={16} xl={24}>
                        {renderEnterpriseTerms(
                           enterpriseNow,
                           <>
                              <b style={{ marginLeft: "5px" }}>Enterprise</b>
                              &nbsp; package
                           </>
                        )}
                     </Col>
                  )}
                  {enterpriseOffer && (
                     <Col xs={24} sm={18} md={12} lg={16} xl={24}>
                        {renderEnterpriseTerms(
                           enterpriseOffer,
                           <b style={{ marginLeft: "5px" }}>New Enterprise terms available</b>
                        )}
                     </Col>
                  )}
                  {!enterpriseNow && !enterpriseOffer && (
                     <Col xs={24} sm={18} md={12} lg={16} xl={24}>
                        {/* <div className={`subscriptionPlans ${isSelected ? "selected" : ""}`}> */}
                        <div className="subscriptionPlans enterprisePlan">
                           {/* No trial line: see the plans above. */}
                           <div className="planTitle">
                              <span
                                 style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexWrap: "wrap"
                                 }}
                              >
                                 <StarRewardRate />
                                 <b style={{ marginLeft: "5px" }}>Enterprise</b>
                                 &nbsp; package
                              </span>
                           </div>
                           <div
                              className="payment"
                              style={{ display: "flex", flexDirection: "column" }}
                           >
                              {/* <span>USD</span>{" "} */}
                              <span
                                 className="annuallyPrice"
                                 style={{
                                    fontSize: "26px",
                                    paddingTop: "20px"
                                 }}
                              >
                                 Contact Us
                              </span>
                              {/* <span style={{ color: "#999999" }}> for pricing</span> */}
                           </div>
                           <div className="content">
                              <p
                                 style={{
                                    // color: isSelected ? "#DE8344" : "#9BACEB",
                                    color: "#9BACEB",
                                    fontFamily: "Poppins",
                                    fontSize: "14px",
                                    fontWeight: "600"
                                 }}
                              >
                                 This plan includes:
                              </p>
                              <p>
                                 <AiOutlineCheckCircle />
                                 &nbsp;
                                 <b>Unlimited</b> workspaces
                              </p>
                              <p>
                                 <AiOutlineCheckCircle />
                                 &nbsp;
                                 <b>Unlimited</b> users/workspace
                              </p>
                              <p>
                                 <AiOutlineCheckCircle />
                                 &nbsp;
                                 <>
                                    <b>Unlimited</b> data storage
                                 </>
                              </p>
                           </div>
                           <div className="enterpriseActions">
                              <PlanDetail
                                 planData={{}}
                                 enterprice={true}
                                 chooseButton={
                                    !billingManaged ? undefined : isEnterpriseRequested ? (
                                       <p className="enterpriseRequested">
                                          Enterprise requested — we'll be in touch
                                       </p>
                                    ) : (
                                       <Button
                                          type="default"
                                          className="defaultButton"
                                          onClick={() => setIsEnterpriseDialogOpen(true)}
                                       >
                                          Request Enterprise
                                       </Button>
                                    )
                                 }
                              />
                           </div>
                        </div>
                     </Col>
                  )}
               </Row>
               {isEnterpriseDialogOpen && (
                  <BasicModal
                     isModalOpen={isEnterpriseDialogOpen}
                     handleCancel={() => setIsEnterpriseDialogOpen(false)}
                     footerButtonCustomStyle
                  >
                     <div className="mt-5 mb-8">
                        <h4>Request Enterprise</h4>
                     </div>
                     <p className="promoCodeDescription">
                        Your current plan carries on. We send your request to our team, who agree the
                        prices and limits of your Enterprise plan with you. You then take them up here.
                     </p>
                     <EnterpriseNeedsField value={enterpriseNeeds} onChange={setEnterpriseNeeds} />
                     <Row gutter={16} className="mt-8" justify="center" align="middle">
                        <Col>
                           <Button
                              className="cancel"
                              onClick={() => setIsEnterpriseDialogOpen(false)}
                              disabled={isRequestingEnterprise}
                           >
                              Cancel
                           </Button>
                        </Col>
                        <Col>
                           <Button
                              onClick={requestEnterprise}
                              className="primaryButton"
                              loading={isRequestingEnterprise}
                           >
                              Send request
                           </Button>
                        </Col>
                     </Row>
                  </BasicModal>
               )}
               {/* Packet prices are before tax: Stripe adds any tax to the invoice. */}
               <p className="taxNote">
                  Prices don't include tax. Any applicable taxes are added to your invoice.
               </p>
            </div>
         )}
      </>
   );
};

export default SubscriptionPlanBoxes;
